/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/promise-function-async */
import React, {
    useState,
    createContext,
    useContext,
    useEffect,
    useCallback,
    useRef,
} from "react";
import {
    assertMainEntryNotLoaded,
    isSearchActive,
    loadPolyfill,
} from "./lazy-utils";

interface ValuSearchModuleBase {
    default: import("../index").ValuSearch;
}

interface LazyValuSearchContextType<Mod> {
    activate: () => void;
    deactivate: () => void;
    load: () => void;
    isActive: boolean;
    isLoading: boolean;
    module: Mod | undefined;
}

interface LazyValuSearchOptions<Mod> {
    instanceId?: string;
    load: () => Promise<Mod>;
    onLoad?: (mod: Mod) => void;
}

export class LazyReactValuSearch<ModuleType extends ValuSearchModuleBase> {
    options: LazyValuSearchOptions<ModuleType>;
    module: ModuleType | undefined;
    instanceId: string;

    context = createContext<LazyValuSearchContextType<ModuleType> | null>(null);

    constructor(options: LazyValuSearchOptions<ModuleType>) {
        this.options = options;
        this.instanceId = options.instanceId || "vs";
    }

    /**
     * Class name used to create FocusTrap containers
     */
    get trapClassName() {
        return `valu-search-focus-trap-${this.instanceId}`;
    }

    /**
     * Render a container div for the FocusTrap
     */
    FocusTrapContainer = React.forwardRef<
        HTMLDivElement,
        { className?: string; children: React.ReactNode }
    >((props, ref) => {
        let className = this.trapClassName;

        if (props.className) {
            className += " " + props.className;
        }

        return (
            <div ref={ref} className={className}>
                {props.children}
            </div>
        );
    });

    get instance() {
        return this.module?.default;
    }

    isActive = () => {
        return isSearchActive(this.options.instanceId);
    };

    deactivate = () => {
        if (this.module) {
            this.module.default.deactivate();
        }
    };

    load = () => {
        assertMainEntryNotLoaded();

        if (this.module) {
            return Promise.resolve(this.module);
        }

        this.isLoading = true;
        this._setLoading?.(true);

        return new Promise<void>((resolve) => {
            loadPolyfill(resolve);
        }).then(() => {
            return this.options.load().then((mod) => {
                if (this.instanceId !== mod.default.instanceId) {
                    throw new Error(
                        `[RVS] The lazy loaded RVS instanceId "${mod.default.instanceId}"  does not match with loader instanceId "${this.instanceId}"`,
                    );
                }

                this.isLoading = false;
                this.module = mod;
                this.options.onLoad?.(mod);
                this._setMod?.(mod);
                this._setLoading?.(false);
                return mod;
            });
        });
    };

    activate = () => {
        if (this.module) {
            this.module.default.activate();
            return;
        }

        this.load().then(
            (mod) => {
                mod.default.activate();
            },
            (error) => {
                console.error("Failed lazy load valu search", error);
            },
        );
    };

    private _setMod?: React.Dispatch<
        React.SetStateAction<ModuleType | undefined>
    >;

    isLoading = false;
    private _setLoading?: React.Dispatch<React.SetStateAction<boolean>>;

    Provider = (props: { children: any }) => {
        const [mod, setMod] = useState<ModuleType | undefined>(this.module);
        this._setMod = setMod;

        const [isLoading, setLoading] = useState(this.isLoading);
        this._setLoading = setLoading;

        const [isActive, setActive] = useState(false);

        // Immediately load and activate the search on page load if the url contains
        // the valu search query string
        useEffect(() => {
            if (this.isActive()) {
                this.activate();
            }
        }, []);

        useEffect(() => {
            const listen = () => {
                setActive(this.isActive());
            };

            window.addEventListener("valuSearchHistoryEvent", listen, false);

            return () => {
                window.removeEventListener(
                    "valuSearchHistoryEvent",
                    listen,
                    false,
                );
            };
        }, [mod]);

        return (
            <this.context.Provider
                value={{
                    module: mod,
                    activate: this.activate,
                    isActive,
                    isLoading,
                    load: this.load,
                    deactivate: this.deactivate,
                }}
            >
                {props.children}
            </this.context.Provider>
        );
    };

    useLazyValuSearch = () => {
        const contextValue = useContext(this.context);

        if (contextValue === null) {
            throw new Error(
                "bug: The Provider for LazyValuSearch not configured",
            );
        }

        return contextValue;
    };

    /**
     * Return input element ref which binds lazy loading the the focus event
     * and after loading binds the input to the search engine
     */
    useInput = () => {
        const vs = this.useLazyValuSearch();

        // Keep the dom reference here
        const ref = useRef<HTMLInputElement | null>(null);

        // Read how useCallback can be used to access refs:
        // https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
        // tl;dr every time useCallback() generates a new callback it will get
        // called again with the ref value OR when the underlying element changes
        return useCallback(
            (input: HTMLInputElement | null) => {
                // No change to the input element. Skip
                if (input === ref.current) {
                    return;
                }

                // Detect stale ref.current when we get new input or null
                if (ref.current && ref.current !== input) {
                    ref.current.removeEventListener("focus", vs.load, false);
                    vs.module?.default.unbindInput(ref.current);
                    ref.current = null;
                }

                // When we get a new element bind lazy loading to the focus
                // event. Also when we have loaded the vs code we bind it to the
                // "engine"
                if (input) {
                    vs.module?.default.bindInput(input);
                    input.addEventListener("focus", vs.load, false);
                    ref.current = input;
                }
            },
            [vs.load, vs.module],
        );
    };
}
